import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { History } from 'lucide-react'
import { getGrazingVerificationMap } from '../services/api'

// One paddock's grazing history over the last year, as the satellite verified it:
// a strip of 2-week periods (oldest → newest), each coloured by that period's
// verdict-vs-calendar state. Reads the rest–graze rhythm at a glance.

const STATE = {
  confirmed:     { c: '#3f8a56', label: 'Grazed (confirmed)' },
  grazed:        { c: '#4f9d8f', label: 'Grazed (satellite)' },
  unlogged:      { c: '#c2572a', label: 'Grazing, unlogged' },
  logged_unseen: { c: '#dea63a', label: 'Logged, not seen' },
  resting:       { c: '#cdd8c6', label: 'Resting' },
  // A period with no usable pass. Distinct from resting on purpose — a gap in
  // the record must not read as a quiet fortnight.
  insufficient_data: { c: '#e5e2dc', label: 'Not verified' },
}

function mon(iso) {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en', { month: 'short' }) + (d.getMonth() === 0 ? ` '${String(d.getFullYear()).slice(2)}` : '')
}

export default function PaddockGrazingTimeline({ ranchId, paddockId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['grazingVmap', ranchId],   // shared cache with the overview map
    queryFn: ({ signal }) => getGrazingVerificationMap(ranchId, { periods: 26, signal }),
    enabled: !!ranchId,
    staleTime: 5 * 60_000,
  })

  const { cells, code, present } = useMemo(() => {
    const p = data?.paddocks?.find(x => x.id === paddockId)
    if (!p || !data?.periods) return { cells: [], code: null, present: {} }
    const rows = [...data.periods].reverse().map(per => ({   // oldest first
      // A period the backend gave us no cell for is unverified, NOT resting —
      // defaulting to resting would draw a quiet fortnight we never observed.
      start: per.start, end: per.end, ...(per.cells[p.code] || { state: 'insufficient_data' }),
    }))
    const pr = {}
    rows.forEach(r => { pr[r.state] = true })
    return { cells: rows, code: p.code, present: pr }
  }, [data, paddockId])

  const lastGrazed = [...cells].reverse().find(c => ['confirmed', 'grazed', 'unlogged'].includes(c.state))

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-center gap-2 mb-1">
        <History className="h-5 w-5 text-green-700" />
        <h2 className="text-lg font-semibold text-gray-900">Grazing history</h2>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        The last year in two-week steps, as the satellite verified it — read the rest–graze rhythm left to right.
      </p>

      {isLoading ? (
        <div className="h-16 animate-pulse bg-gray-50 rounded" />
      ) : cells.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">Not enough satellite history yet for this paddock.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <div className="flex items-end gap-[3px] min-w-max pb-1">
              {cells.map((c, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div
                    className="rounded-sm"
                    style={{ width: 16, height: 26,
                             background: c.state === 'insufficient_data'
                               ? `repeating-linear-gradient(45deg, ${STATE.insufficient_data.c} 0 2px, #a9a396 2px 4px)`
                               : (STATE[c.state]?.c || STATE.insufficient_data.c),
                             opacity: c.state === 'resting' ? 0.6 : 0.95 }}
                    title={`${c.start} – ${c.end}: ${STATE[c.state]?.label || c.state}${c.verdict != null ? ` (verdict ${c.verdict > 0 ? '+' : ''}${c.verdict})` : ''}`}
                  />
                  <span className="text-[8px] text-gray-400 mt-1 font-mono h-3">
                    {i % 2 === 0 ? mon(c.end) : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
            {Object.keys(STATE).filter(k => present[k]).map(k => (
              <span key={k} className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: STATE[k].c }} /> {STATE[k].label}
              </span>
            ))}
          </div>
          {lastGrazed && (
            <p className="text-xs text-gray-500 mt-3">
              Most recent grazing the satellite saw: <b className="text-gray-700">{mon(lastGrazed.end)}</b>
              {' '}({new Date(lastGrazed.end + 'T00:00:00').toLocaleDateString()}).
            </p>
          )}
        </>
      )}
    </div>
  )
}
